import { getProfileImage } from "@shared/hooks/useProfile";

import { scrollToTop } from "@shared/utils/scroll";
import theme from "@shared/utils/themeColors";
import { useState } from "react";
import { usePostStore } from "../../store/postStore";
import { Post } from "../../types/post.types";

interface SharePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  onShare?: (content?: string) => Promise<void>;
}

export default function SharePostModal({
  isOpen,
  onClose,
  post,
  onShare,
}: SharePostModalProps) {
  const [content, setContent] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const { sharePost } = usePostStore();

  const handleShare = async () => {
    try {
      setIsSharing(true);
      if (onShare) {
        await onShare(content);
      } else {
        await sharePost(post.id!, content);
      }
      onClose();
      // Scroll to top smoothly
      scrollToTop();
    } catch (error) {
      console.error("Error sharing post:", error);
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div
          className={`inline-block transform overflow-hidden rounded-lg ${theme.bg.primary} px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle`}
        >
          <div>
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                Share Post
              </h3>
              <div className="mt-4">
                <textarea
                  className={`w-full rounded-md ${theme.border.secondary} shadow-sm ${theme.border.focus} ${theme.border.ring}  dark:bg-gray-700 dark:text-gray-100`}
                  rows={3}
                  placeholder="Add a comment (optional)"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                />
              </div>

              {/* Original Post Preview */}
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-start space-x-3">
                  <img
                    className="h-10 w-10 rounded-full"
                    src={getProfileImage(post.author)}
                    alt={post.author?.name}
                  />
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-gray-100">
                      {post.author?.name}
                    </p>
                    <p className="mt-1 text-gray-600 dark:text-gray-300">
                      {post.content}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className={`ml-3 ${theme.button.primary}`}
              onClick={handleShare}
              disabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share"}
            </button>
            <button
              type="button"
              className={`${theme.button.secondary}`}
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
